import React, { FC, useMemo, useEffect } from 'react';
import { buildSemSearchRequest, parseSemSearchParams } from './parsers';
import { useUrlParams } from 'clientState/hooks/useUrlParams';
import { useSemLocations } from 'clientState/providers/SEM/SemLocationsProvider';
import {
  GeoServiceLocation,
  SemItineraryRequest,
  SemSearchRequest,
} from '../../../../generated/graphql';
import {
  PerformedSearchRequest,
  usePerformedSearchRequest,
} from '../../SearchRequestProvider';
import { usePerformedSemSearchHash } from '../SemSearchRequestHashProvider';
import { useFlightDataLayer } from 'clientState/hooks/SEM/useFlightDataLayer';
import { useSearchTracker } from '../../SearchTrackerProvider';

export type SemSearchRequestContextType = {
  tripType: string | null;
  itinerary: SemItineraryRequest | null;
};

export type SemSearchParamsType = {
  tripType: string;
  from: GeoServiceLocation;
  to: GeoServiceLocation;
  buyPath?: string;
  adults?: number;
  children?: number;
  infants?: number;
};

const SemSearchRequestProvider: FC = ({ children }) => {
  const { startSearch } = useSearchTracker();
  const hash = usePerformedSemSearchHash();
  const urlParams = useUrlParams(hash);
  const {
    type = 'R',
    from,
    to,
    adults: urlAdults,
    children: urlChildren,
    infants: urlInfants,
  } = urlParams;
  const { locations } = useSemLocations();
  const departure = from ? locations[from] : null;
  const destination = to ? locations[to] : null;
  const performedSemSearchRequest = useMemo(() => {
    if (departure && destination) {
      const semSearchParamsObject: SemSearchParamsType = {
        tripType: type,
        from: departure,
        to: destination,
        adults: urlAdults ? parseInt(urlAdults) : undefined,
        children: urlChildren ? parseInt(urlChildren) : undefined,
        infants: urlInfants ? parseInt(urlInfants) : undefined,
      };
      return parseSemSearchParams(semSearchParamsObject);
    }
  }, [departure, destination, type, urlAdults, urlChildren, urlInfants]);

  useEffect(() => {
    if (departure && destination) {
      startSearch();
    }
  }, [departure, destination, startSearch]);

  useFlightDataLayer();

  return (
    <PerformedSearchRequest.Provider value={performedSemSearchRequest || null}>
      {children}
    </PerformedSearchRequest.Provider>
  );
};

export const useSemSearchQuery = (): SemSearchRequest | null => {
  const semSearchParams = usePerformedSearchRequest();
  return !semSearchParams ? null : buildSemSearchRequest(semSearchParams);
};

export default SemSearchRequestProvider;
